import ViewExtensionViewModel from '@/ViewModels/Views/ViewExtensionViewModel';
import ComponentManager from '@/Management/ComponentManager';
import SearchBoxViewModel from '@/Components/SearchView/SearchBox/SearchBoxViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import AutoCompleteViewModel from '@/ViewModels/Controls/AutoCompleteViewModel';
import ImageViewModel from '@/ViewModels/Controls/ImageViewModel';
import ButtonViewModel from '@/ViewModels/Controls/ButtonViewModel';
import SearchGridViewModel from '@/Components/SearchView/SearchGrid/SearchGridViewModel';
import ToolBarViewModel from '@/ViewModels/Controls/ToolBarViewModel';
import CustomContext from '@/ViewModels/Contexts/CustomContext';
import RADApplication from '@/Management/RADApplication';
import NavigationHelper from '@/Management/NavigationHelper';
import StorageHelper from '@/Utility/StorageHelper';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import NavigationService from '@/Management/NavigationService';
import { ISearchParameterDTO } from "coreframetypings/dto";
import Config from '@/Management/GlobalConfiguration';
import SearchViewFactory from '@/Components/SearchView/SearchViewFactory';
import ExpanderBoxViewModel from '@/ViewModels/Controls/ExpanderBoxViewModel';
import ExpanderBoxViewModelBuilder from '@/Builder/ExpanderBoxViewModelBuilder';
import SplitterViewModel from '@/ViewModels/Controls/SplitterViewModel';
import SplitterPaneViewModel from '@/ViewModels/Controls/SplitterPaneViewModel';
import SplitterPaneDividerViewModel from '@/ViewModels/Controls/SplitterPaneDividerViewModel';
import LocatorHelper from '@/Utility/LocatorHelper';
import { ISearchViewViewModelParameters, SearchViewSearchResult } from '#/DefaultViewExtensionInterfaces';

export default class SearchViewViewModel extends ViewExtensionViewModel {
    private SearchBoxViewModel = ComponentManager.getViewModel<typeof SearchBoxViewModel>('SearchBoxViewModel');
    private LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModel>('LinkViewModel');
    private AutoCompleteViewModel = ComponentManager.getViewModel<typeof AutoCompleteViewModel>('AutoCompleteViewModel');
    private ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
    private ButtonViewModel = ComponentManager.getViewModel<typeof ButtonViewModel>('ButtonViewModel');
    private SearchGridViewModel = ComponentManager.getViewModel<typeof SearchGridViewModel>('SearchGridViewModel');
    private ToolBarViewModel = ComponentManager.getViewModel<typeof ToolBarViewModel>('ToolBarViewModel');
    private CustomContext = ComponentManager.getViewModel<typeof CustomContext>('CustomContext');
    private SplitterViewModel = ComponentManager.getViewModel<typeof SplitterViewModel>('SplitterViewModel');
    private SplitterPaneViewModel = ComponentManager.getViewModel<typeof SplitterPaneViewModel>('SplitterPaneViewModel');
    private SplitterPaneDividerViewModel = ComponentManager.getViewModel<typeof SplitterPaneDividerViewModel>('SplitterPaneDividerViewModel');

    /**
     * @override
     */
    public viewFactory: SearchViewFactory;
    
    public context: CustomContext;
    public searchParameter: KnockoutObservable<ISearchParameterDTO>;
    public searchGrid: SearchGridViewModel;
    public lastSearchKeyword: KnockoutComputed<string>;
    public button: ButtonViewModel;
    public image: ImageViewModel;
    public toolbar: ToolBarViewModel;
    public searchBoxLabel: LabelViewModel;
    public searchBox: SearchBoxViewModel;
    public searchExpander: ExpanderBoxViewModel;
    public resultsExpander: ExpanderBoxViewModel;
    public splitter: SplitterViewModel;
    public searchGridSplitterPane: SplitterPaneViewModel;
    public summaryViewSplitterPane: SplitterPaneViewModel;
    public summaryViewIsManuallyClosed: KnockoutObservable<boolean>;
    private loadData: KnockoutObservable<boolean>;
    public selectedSearchResult: KnockoutObservable<SearchViewSearchResult>;
    public showBreadCrumb: KnockoutObservable<boolean>;
    
    constructor(params: ISearchViewViewModelParameters) {
        super($.extend({ headerErgName: 'SEARCH_TITLE' }, params));

        this.context = new this.CustomContext({});
        this.searchParameter = this.observableOrDefault(null);
        this.searchGrid = this.createSearchGrid();
        this.lastSearchKeyword = this.createLastSearchKeywordComputed();
        this.image = this.createImage();
        this.toolbar = this.createToolbar();
        this.loadData = this.observableOrDefault(false);
        this.searchBoxLabel = this.createSearchBoxLabel();
        this.searchBox = this.createSearchBox();
        this.button = this.createButton();
        this.searchExpander = this.createSearchExpander();
        this.resultsExpander = this.createResultsExpander();
        this.selectedSearchResult = this.observableOrDefault<SearchViewSearchResult>(null);
        this.summaryViewIsManuallyClosed = this.observableOrDefault(false);
        this.searchGridSplitterPane = this.createSearchGridSplitterPane();
        this.summaryViewSplitterPane = this.createSummaryViewSplitterPane();
        this.splitter = this.createSplitter();
        this.showBreadCrumb = this.observableOrDefault(true);

        this.initialize();
    }
    
    private initialize(): void {
        RADEnvironmentProxy.getInstance().getAvailableSearchParameters()
            .then(searchParameterDTO => {
                if (this.isDisposed === false) {
                    this.onSearchParameterLoaded(searchParameterDTO);
                }
            });
    }

    private createSearchGrid() {
        const columnPreset = StorageHelper.bind('Search_ColumnPreset', {});

        this.addComputed(columnPreset);
        return new this.SearchGridViewModel({
            parentLocator: this.locator,
            locator: 'searchgrid',
            pageSizes: RADApplication.getInstance().configuration.possibleListViewPages,
            pageSize: this.viewFactory.pageSize,
            page: this.viewFactory.page,
            rowLimit: 0,
            columnPreset: columnPreset,
            identity: 'pdoId',
            height: '100%',
            onDoubleClick: (pdoId, row, openInNewWindow) => {
                const element = _.find(this.searchGrid.data(), data => data.pdoId === pdoId);
                this.openSearchResult(element, openInNewWindow);
            },
            onSelectionChangedHandler: rows => {
                if (!rows || !rows.length) {
                    this.selectedSearchResult(null);
                    return;
                }

                const firstRow = rows[0];
                const searchResult: SearchViewSearchResult = {
                    pdoId: firstRow.pdoId,
                    pdTypeName: firstRow.pdTypeName
                };
                this.selectedSearchResult(searchResult);
            },
            tooltipOptions: {
                show: false,
            }
        });
    }
    
    private createLastSearchKeywordComputed(): KnockoutComputed<string> {
        return this.defineComputed(() => {
            const queryObject = this.searchGrid.lastSearchQuery();

            if (queryObject === null || queryObject === undefined)
                return null;

            return queryObject.query;
        });
    }
    
    private createImage(): ImageViewModel {
        return new this.ImageViewModel({
            parentLocator: this.locator,
            locator: 'search-image',
            source: 'index_view.png'
        });
    }
    
    private createSearchBoxLabel(): LabelViewModel {
        return new this.LabelViewModel({
            ergName: 'SEARCH_TEXTBOX_LABEL'
        });
    }
    
    private createSearchBox(): SearchBoxViewModel {
        return new this.SearchBoxViewModel({
            parentLocator: this.locator,
            locator: 'searchbox',
            autoComplete: new this.AutoCompleteViewModel({
                parentLocator: this.locator,
                locator: 'searchbox.autocomplete',
                data: this.defineAsyncComputed(() => {
                    if (this.isDisposed || !this.loadData())
                        return [];
                    const pageSize = this.searchGrid.pageSize();
                    const query = this.searchBox.autoComplete.value();
                    // don't trigger search if popup is open
                    if (query && query.length >= 3 && !this.searchBox.isPopupOpen.peek()) {
                        const searchQuery = this.searchBox.buildSearchQuery(pageSize, query);
                        this.searchGrid.searchQuery(searchQuery);

                        return RADEnvironmentProxy.getInstance().getSearchSuggestions({
                            query: searchQuery
                        }).then((data) => {
                            if (this.validateStatus(data)) {
                                return data.suggestions;
                            }

                            return [];
                        });
                    } else if (!query && this.searchBox)
                        return this.searchBox.lastSearchValues();
                    else
                        return [];
                }),
                onKeyUp: () => {
                    this.searchBox.autoComplete.data.evaluate();
                },
                value: this.observableOrDefault(this.viewFactory.query || '').extend({ rateLimit: 100 }),
                filter: () => {
                    return true;
                },
                maxLength: 255,
                tooltip: new this.LabelViewModel({
                    ergName: 'SEARCH_INPUT_INFO'
                }),
                onSubmit: (query) => {
                    this.searchBox.onSubmit(query);
                },
                minLength: 0,
                searchOnFocus: true
            }),
            onSubmit: query => {
                const pageSize = this.searchGrid.pageSize();
                const searchQuery = this.searchBox.buildSearchQuery(pageSize);

                this.searchGrid.searchQuery(searchQuery);
                this.searchGrid.refresh();
                this.selectedSearchResult(null);

                NavigationService.getInstance().setParametersToAdress(this.identifier, {
                    Query: query
                }, false);

                NavigationService.getInstance().setHiddenParameterToAddress(this.identifier, 'SearchQuery', searchQuery);
            }
        });
    }
    
    private createButton(): ButtonViewModel {
        return new this.ButtonViewModel({
            parentLocator: this.locator,
            locator: 'start-search',
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'start-search.image',
                source: Config.inlineImages.MAGNIFYING_GLASS,
                alternativeText: new this.LabelViewModel({
                    ergName: 'SEARCH'
                })
            }),
            label: new this.LabelViewModel({
                parentLocator: this.locator,
                locator: 'start-search.label',
                ergName: 'SEARCH'
            }),
            onClick: () => {
                this.searchBox.isPopupOpen(false);
                this.searchBox.onSubmit(this.searchBox.autoComplete.value());
            },
            tooltip: new this.LabelViewModel({
                ergName: 'SEARCH_BUTTON_INFO'
            }),
            isEnabled: this.searchBox.isSearchEnabled
        });
    }
    
    private createSearchExpander(): ExpanderBoxViewModel {
        return new ExpanderBoxViewModelBuilder()
            .withLink()
                .withLabel()
                    .withErgName('SEARCH')
                    .done()
                .done()
            .withParentLocator(this.locator)
            .withLocator('results-label')
            .build();
    }
    
    private createResultsExpander(): ExpanderBoxViewModel {
        return new ExpanderBoxViewModelBuilder()
            .withLink()
                .withLabel()
                    .withErgName('SEARCH_RESULTS')
                    .done()
                .done()
            .withParentLocator(this.locator)
            .withLocator('results-expander')
            .build();
    }
    
    protected createSearchGridSplitterPane(): SplitterPaneViewModel {
        return new this.SplitterPaneViewModel({
            isExpanded: true,
            isCollapsible: false,
            isResizable: true,
            isScrollable: false,
            collapsedSize: null,
            size: null,
            min: "0",
            max: null,
            parentLocator: this.locator,
            locator: LocatorHelper.buildLocator(this.locator, 'search', 'splitterpane', 'seachgrid')
        });
    }

    protected createSummaryViewSplitterPane(): SplitterPaneViewModel {
        const size = StorageHelper.bind('Search_SummaryViewScreenRatio', '35%');
        this.addComputed(size);
        return new this.SplitterPaneViewModel({
            isExpanded: ko.computed({
                read: () => {
                    const manuallyClosed = this.summaryViewIsManuallyClosed();
                    const selectedSearchResult = this.selectedSearchResult();

                    if (manuallyClosed)
                        return false;

                    return selectedSearchResult != null;
                },
                write: val => {
                    this.summaryViewIsManuallyClosed(!val);
                }
            }),
            isCollapsible: true,
            isResizable: true,
            isScrollable: true,
            collapsedSize: 0,
            size: size,
            min: "0",
            max: null,
            parentLocator: this.locator,
            locator: LocatorHelper.buildLocator(this.locator, 'search', 'splitterpane', 'summaryview')
        });
    }
    
    private createSplitter(): SplitterViewModel {
        const divider = new this.SplitterPaneDividerViewModel({
            attachedTo: this.summaryViewSplitterPane
        });
        return new this.SplitterViewModel({
            isHorizontal: true,
            panes: [
                this.searchGridSplitterPane,
                this.summaryViewSplitterPane
            ],
            dividers: [
                divider
            ],
            isRequired: this.defineComputed(() => {
                // Required steuert auch die Sichtbarkeit: Splitter wird nicht "benötigt", 
                // wenn kein Suchergebnis ausgewählt ist und somit auch keine SV angezeigt wird.
                const selectedSearchResult = this.selectedSearchResult();
                return selectedSearchResult != null;
            })
        });
    }
    
    private createToolbar(): ToolBarViewModel {
        const toolbarLabel = (params) => {
            params.isVisible = Config.listView.ShowToolbarText;
            return new this.LabelViewModel(params);
        };

        return new this.ToolBarViewModel({
            parentLocator: this.locator,
            locator: "toolbar",
            primary: ko.observableArray([
                new this.LinkViewModel({
                    parentLocator: this.locator,
                    locator: "toolbar.edit",
                    isActive: false,
                    isVisible: true,
                    isEnabled: this.defineComputed(() => {
                        const selected = this.searchGrid.selected();
                        return selected.length > 0;
                    }),
                    image: new this.ImageViewModel({
                        source: Config.inlineImages.TOOLBAR_EDIT,
                        parentLocator: this.locator,
                        locator: "toolbar.edit.image",
                        alternativeText: new this.LabelViewModel({
                            ergName: 'EDIT_CONNECTED_TOOLTIP'
                        })
                    }),
                    label: toolbarLabel({
                        ergName: 'EDIT_CONNECTED_TOOLTIP',
                        parentLocator: this.locator,
                        locator: "toolbar.edit.label"
                    }),
                    tooltip: new this.LabelViewModel({
                        ergName: 'EDIT_CONNECTED_TOOLTIP'
                    }),
                    onClick: () => {
                        const data = this.searchGrid.data();
                        const selected = this.searchGrid.selected();
                        let element = null;

                        for (let i = 0; i < data.length; i++) {
                            if (data[i][this.searchGrid.identity] === selected[0]) {
                                element = data[i];
                                break;
                            }
                        }

                        this.openSearchResult(element, false);
                    }
                })
            ])
        });
    }
    
    public openSearchResult(element: any, openInNewWindow: boolean) {
        if (element == null) {
            return;
        }

        if (RADApplication.getInstance().isSystemParamSetAndTrue('Gui.Search.OpenSummaryView')) {
            if (element.isHistorized && element.historizationGroupPDO_ID > 0) {
                NavigationHelper.openListViewByType(element.pdTypeName, {
                    contextSource: 'Search',
                    contextTypeName: 'RAD.RADFoundation.ProblemDomain.History.HistorizationGroup',
                    contextObject: element.historizationGroupPDO_ID,
                    contextRelation: 'Subjects',
                    principalFilterMode: 'PRINCIPAL_SELECTOR_ALL',
                    selectedPDO: element.pdoId
                }, openInNewWindow);
            }
            else {
                const principalFilter = this.includeAllPrincipals() ? 'PRINCIPAL_SELECTOR_ALL' : 'Default';
                NavigationHelper.openListViewByType(element.pdTypeName, { selectedPDO: element.pdoId, principalFilterMode: principalFilter }, openInNewWindow);
            }
        } else {
            NavigationHelper.openDetailView('EDIT', element.pdTypeName, { pdoId: element.pdoId }, openInNewWindow);
        }
    }

    private includeAllPrincipals() {
        if (this.searchGrid.isDisposed)
            return false;
        const query = this.searchGrid.searchQuery();
        if (!query)
            return false;

        return query.searchInAllPrincipals;
    }

    public onSearchParameterLoaded(searchParameterDTO: ISearchParameterDTO) {
        this.searchBox.extensionModules(searchParameterDTO.extensionModules);
        this.searchBox.historizationPoints(searchParameterDTO.historizationPoints);
        this.searchBox.setSearchQuery(this.viewFactory.searchQuery);
        
        this.searchParameter(searchParameterDTO);

        this.searchGrid.init();

        this.loadData(true);

        if (this.viewFactory.query != null) {
            this.searchBox.onSubmit(this.searchBox.autoComplete.value());
        }
    }
}

ComponentManager.setViewModel('SearchViewViewModel', SearchViewViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');